/**
 * useExpandableOrders
 *
 * State management hook for expandable order rows.
 * Manages expand/collapse state for the order list with support for
 * expand all / collapse all functionality.
 */

import { useState, useCallback, useMemo } from 'react';

export interface UseExpandableOrdersReturn {
  /** Set of currently expanded order IDs */
  expandedIds: Set<string>;
  /** Check if a specific order is expanded */
  isExpanded: (id: string) => boolean;
  /** Toggle expand state for a single order */
  toggleExpand: (id: string) => void;
  /** Expand all orders (pass array of all order IDs) */
  expandAll: (ids: string[]) => void;
  /** Collapse all orders */
  collapseAll: () => void;
  /** True if all provided IDs are expanded */
  allExpanded: boolean;
  /** True if no orders are expanded */
  noneExpanded: boolean;
}

interface UseExpandableOrdersOptions {
  /** Order IDs to track for allExpanded calculation */
  orderIds?: string[];
  /** Initially expanded order IDs */
  initialExpanded?: string[];
}

export function useExpandableOrders(
  options: UseExpandableOrdersOptions = {}
): UseExpandableOrdersReturn {
  const { orderIds = [], initialExpanded = [] } = options;

  const [expandedIds, setExpandedIds] = useState<Set<string>>(
    () => new Set(initialExpanded)
  );

  const isExpanded = useCallback(
    (id: string) => expandedIds.has(id),
    [expandedIds]
  );

  const toggleExpand = useCallback((id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  const expandAll = useCallback((ids: string[]) => {
    setExpandedIds(new Set(ids));
  }, []);

  const collapseAll = useCallback(() => {
    setExpandedIds(new Set());
  }, []);

  const allExpanded = useMemo(() => {
    if (orderIds.length === 0) return false;
    return orderIds.every((id) => expandedIds.has(id));
  }, [orderIds, expandedIds]);

  const noneExpanded = useMemo(() => {
    return expandedIds.size === 0;
  }, [expandedIds]);

  return {
    expandedIds,
    isExpanded,
    toggleExpand,
    expandAll,
    collapseAll,
    allExpanded,
    noneExpanded,
  };
}
